'use client';

import React, { createContext, useContext } from 'react';

// Faza 0 (docs/SPEC_SERVICE_LOGISTICS_SUBSCRIPTIONS.md section 6.1): both (store)/layout.tsx and
// admin/layout.tsx already fetch the public /site-config payload server-side via getSiteConfig()
// — this hands that data to client components via context instead of each of them independently
// re-fetching it (previously ~8 separate storefront-facing components each firing their own
// uncached fetchAPI('/site-config') on mount).
//
// Both layouts pass initialConfig through pickSiteConfigClientFields() (core/config/
// siteConfigClientFields.ts), NOT the raw config object — the full public payload is ~90 fields /
// ~12KB for a typical tenant (mostly header_config/footer_config, which reach the page through
// their own direct server-rendered props instead), and passing that whole object into a client
// component prop serializes all of it into every page's RSC payload whether any consumer reads it
// or not. Found live via PageSpeed regression on shopstag-ci after this provider first shipped —
// keep initialConfig on the trimmed allowlist, don't widen it back to the full config object.
//
// NOT a substitute for a component that needs config to react to a *client-side* currency change
// without a page reload — CheckoutBlock.tsx deliberately keeps its own independent, currency-keyed
// fetch (its free_delivery_threshold_display must stay accurate after setCurrencyQuiet() changes
// the display currency mid-checkout, e.g. when the shopper picks a different shipping country;
// this static, SSR-seeded context intentionally does not re-fetch on the client).
type SiteConfig = Record<string, any>;

const SiteConfigContext = createContext<SiteConfig | undefined>(undefined);

export function SiteConfigProvider({
  children,
  initialConfig,
}: {
  children: React.ReactNode;
  initialConfig: SiteConfig | null;
}) {
  return (
    <SiteConfigContext.Provider value={initialConfig || {}}>
      {children}
    </SiteConfigContext.Provider>
  );
}

// Unlike useTranslation() (which throws outside its provider), this returns {} instead of
// throwing when no provider is present. Both (store)/layout.tsx and admin/layout.tsx do wrap
// their trees in this provider now, but the graceful fallback stays as a safety net for any
// future render path (e.g. a standalone route) that renders one of these shared Builder blocks
// without going through either layout. Every consumer already handled a null/not-yet-loaded
// siteConfig before this provider existed (`useState<any>(null)`), so {} is a drop-in equivalent
// of that same "not available yet" state.
export function useSiteConfig(): SiteConfig {
  const context = useContext(SiteConfigContext);
  return context === undefined ? {} : context;
}
